<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
import { DotLottieVue, DotLottieVueInstance, DotLottieVueProps } from '@lottiefiles/dotlottie-vue'
import type { DotLottie } from '@lottiefiles/dotlottie-web'

/**
 * Usage
 * https://developers.lottiefiles.com/docs/dotlottie-player/dotlottie-vue/
 *
 * By default animation is loaded and autoplay is setted by true (if not overrided on parent), and loop to false (if not overrided)
 *
 * Lottie.vue exposes controller so on parent when can access all methods of DotLottie (play, pause, stop, setMode ...),
 * when is needed control to animation play state (playing programatically ... etc)
 *
 * # For example, on parent component #
 * const lottie = ref<InstanceType<typeof Lottie> | null>(null)
 *
 * <Lottie ref="lottie" :lottie-key="lottieKey" @ready="onReady" ... />
 *
 * Handle event @ready from Lottie component when lottie is loaded and player ready
 *
 * const onReady = async () => {
    if (!lottie.value?.controller) return
    // pause in 1s
    await new Promise((resolve) => setTimeout(resolve, 1000))
    lottie.value.controller.pause()
    // play on reverse in 1s
    await new Promise((resolve) => setTimeout(resolve, 1000))
    lottie.value.controller.setMode(
      lottie.value.controller.mode === 'reverse' ? 'forward' : 'reverse'
    )
    lottie.value.controller.play()
  }
  */

const emit = defineEmits<{
  ready: []
  play: []
  stop: []
  loop: []
  complete: []
}>()

const props = withDefaults(
  defineProps<
    DotLottieVueProps & {
      lottieKey: string
    }
  >(),
  {
    autoplay: true,
    loop: false
  }
)

const lottieSrc = `lottie/${props.lottieKey}.lottie`

const playerRef = useTemplateRef<DotLottieVueInstance>('playerRef')
const controller = ref<DotLottie>()

const onReady = () => {
  // trick to resize canvas 1/1 with canvas rendered width
  if (controller.value) {
    const canvas = controller.value.canvas
    if (canvas && 'getBoundingClientRect' in canvas) {
      const canvasWidth = canvas.getBoundingClientRect().width
      canvas.style.height = `${canvasWidth || canvas.width}px`
      controller.value.resize()
    }
  }
  emit('ready')
}

onMounted(() => {
  if (!playerRef.value) return
  const dotLottie: DotLottie | null = playerRef.value.getDotLottieInstance()
  if (!dotLottie) return
  controller.value = dotLottie
  controller.value.addEventListener('ready', () => onReady())
  controller.value.addEventListener('play', () => emit('play'))
  controller.value.addEventListener('stop', () => emit('stop'))
  controller.value.addEventListener('loop', () => emit('loop'))
  controller.value.addEventListener('complete', () => emit('complete'))
})

onBeforeUnmount(() => {
  if (!controller.value) return
  controller.value.removeEventListener('ready')
  controller.value.removeEventListener('play')
  controller.value.removeEventListener('stop')
  controller.value.removeEventListener('complete')
})

defineExpose({ controller })
</script>

<template>
  <DotLottieVue ref="playerRef" v-bind="props" :src="lottieSrc" />
</template>
